import React, { memo, useMemo } from 'react';
import { getComponentStatus, type InventoryStatus } from './SystemComponentCard';
import './connectorGroupCard.css';


interface ConnectorGroupCardProps {
  group: IInventoryConnectorGroup;
  iconClassName?: string;
}

const isEmptyValue = (value: string | null | undefined): boolean =>
  value === null || value === undefined || value.trim() === '' || value.trim() === '-' || value.trim().toLowerCase() === 'na';

const STATUS_LABEL: Record<InventoryStatus, string> = {
  DETECTED: 'DETECTED',
  NO_DATA: 'NO DATA',
  CHECK: 'CHECK',
};

const ConnectorGroupCard = memo(function ConnectorGroupCard({ group }: ConnectorGroupCardProps) {
  const connectors = useMemo(() => group?.connectors ?? [], [group?.connectors]);

  // Header status is derived from every parameter across all connectors.
  const status = useMemo(
    () => getComponentStatus(connectors.flatMap((c) => c.parameters ?? [])),
    [connectors],
  );

  const unitCount = group?.unitCount ?? connectors.length;

  return (
    <div className='inventory__connector__card' id={`inventory-connector-card-${group?.id}`}>
      <div className='inventory__connector__card__header'>
        <div className='inventory__connector__card__title__wrap'>
          <span className='inventory__connector__card__title'>{group?.label}</span>
          <span className='inventory__connector__card__count'>{unitCount} units</span>
        </div>
        <div className={`inventory__connector__card__badge inventory__connector__card__badge--${status.toLowerCase()}`}>
          <span className='inventory__connector__card__badge__marker' />
          {STATUS_LABEL[status]}
        </div>
      </div>

      <div className='inventory__connector__card__body'>
        {connectors.map((connector) => (
          <div className='inventory__connector__block' key={connector.id}>
            <div className='inventory__connector__block__label'>{connector.label}</div>
            <div className='inventory__connector__block__params'>
              {(connector.parameters ?? []).map((param) => {
                const empty = isEmptyValue(param.value);
                return (
                  <div className='inventory__connector__param' key={param.key}>
                    <span className='inventory__connector__param__label'>{param.visibleName}</span>
                    <span
                      className={
                        'inventory__connector__param__value' +
                        (empty ? ' inventory__connector__param__value--empty' : '')
                      }
                    >
                      {empty ? '—' : param.value}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});

export default ConnectorGroupCard;
